import React, { useEffect, useState } from 'react'
import { Button, Select, Spinner, Typography } from '@telavita-core/react-design-kit'
import { addWeeks, formatISO } from 'date-fns'

import { Reservation } from 'models/Reservation'
import reservationService, { ProfessionalRestrictions } from 'services/reservation/'
import { NonRecurrentReservationBox } from 'components'
import { WeekDayOption, useGetReservationAvailabilities } from '../../hooks/useGetReservationAvailabilities'
import { useUpdateReservation } from 'hooks'

import * as S from './styles'

interface ReservationDateSuccess {
  newRecurrentStartDate: Date,
  exceptions: { oldDate: string, newDate: string }[]
}

interface RescheduleTotalRecurrentReservationContentProps {
  reservation: Reservation
  handleSuccess: (newReservationDate: ReservationDateSuccess) => void
}

interface HourOption {
  id: number
  label: string
  startDate: Date
  endDate: Date
}

export const RescheduleTotalRecurrentReservationContent:
  React.FC<RescheduleTotalRecurrentReservationContentProps> = ({ 
    reservation, 
    handleSuccess 
  }) => {
    const [selectedWeekDay, setSelectedWeekDay] = useState<WeekDayOption>(null)
    const [selectedHour, setSelectedHour] = useState<HourOption>(null)
    const [loadingProfessionalRestrictions, setLoadingProfessionalRestrictions] = useState(false)
    const [restrictions, setRestrictions] = useState<Date[]>([])
    const [professionalRestrictions, setProfessionalRestrictions] = useState<ProfessionalRestrictions>({} as ProfessionalRestrictions)
    const [nonRecurrentReservationDates, setNonRecurrentReservationDates] = useState<string[]>([])
    const [disableRescheduleReservationButton, setDisableRescheduleReservationButton] = useState(false)
    
    const { patientUsername, professionalUsername } = reservation

    const { dayOptions, isLoading } = useGetReservationAvailabilities({
      patientUsername,
      professionalUsername
    })
    
    const updateReservation = useUpdateReservation()

    const formattedNonRecurrentReservationDatesForUpdate = restrictions.map((date, index) => {
      const indexedNonRecurrentReservationDates = nonRecurrentReservationDates[index]
      return {
        oldDate: formatISO(date),
        newDate: indexedNonRecurrentReservationDates && formatISO(new Date(indexedNonRecurrentReservationDates))
      }})
      
    function handleUpdateReservation() {
      return updateReservation.mutate({
        uuid: reservation.uuid,
        nonRecurrentReservationDates: nonRecurrentReservationDates.length > 0 && formattedNonRecurrentReservationDatesForUpdate,
        professionalUsername,
        patientUsername,
        recurrentStartDate: formatISO(professionalRestrictions.firstAvailableStartDate),
        recurrentEndDate: formatISO(professionalRestrictions.firstAvailableEndDate)
      })
    }

    function handleNonRecurrentReservationDate(date: string, slotIndex: number) {
      const newNonRecurrentReservationDates = [...nonRecurrentReservationDates]
      newNonRecurrentReservationDates[slotIndex] = date
      setNonRecurrentReservationDates(newNonRecurrentReservationDates)
    }

    const showSubmitButton = selectedWeekDay && selectedHour && !loadingProfessionalRestrictions
    const hasProfessionalRestrictions = restrictions.length > 0
    const hasNonRecurrentReservationDates = nonRecurrentReservationDates
      .every(date => Boolean(date)) && nonRecurrentReservationDates.length > 0

    useEffect(() => {
      if (hasProfessionalRestrictions || !hasNonRecurrentReservationDates) {
        setDisableRescheduleReservationButton(true)
      } 
  
      if ((hasProfessionalRestrictions && hasNonRecurrentReservationDates) || !hasProfessionalRestrictions) {
        setDisableRescheduleReservationButton(false)
      }
  
    }, [
      nonRecurrentReservationDates, 
      restrictions,
      hasNonRecurrentReservationDates, 
      hasProfessionalRestrictions
    ])

    useEffect(() => {
      if (!selectedWeekDay || !selectedHour) return
  
      async function verifyProfessionalRestrictions() {
        setLoadingProfessionalRestrictions(true)
        
        const response = await reservationService.professionalRestrictions({
          professionalUsername,
          patientUsername,
          startDate: addWeeks(selectedHour.startDate, 1),
          endDate: addWeeks(selectedHour.endDate, 1)
        })
  
        setRestrictions(response.restrictions)
        setProfessionalRestrictions(response)
        setLoadingProfessionalRestrictions(false)
      }
  
      void verifyProfessionalRestrictions()
    }, [selectedWeekDay, selectedHour, patientUsername, professionalUsername])
    
    // Reset na hora selecionada quando o dia da semana é alterado
    useEffect(() => {
      if (selectedWeekDay) {
        setSelectedHour(null)
      }
    }, [selectedWeekDay])

    useEffect(() => {
      if (updateReservation.isSuccess) {
        handleSuccess({
          newRecurrentStartDate: professionalRestrictions.firstAvailableStartDate,
          exceptions: formattedNonRecurrentReservationDatesForUpdate,
        })
      }
    }, [
      handleSuccess, 
      updateReservation.isSuccess, 
      formattedNonRecurrentReservationDatesForUpdate, 
      professionalRestrictions.firstAvailableStartDate
    ])
    
    if (isLoading) {
      return (
        <S.SpinnerWrapper>
          <Spinner />
        </S.SpinnerWrapper>
      )
    }

    return (
      <S.Container>
        <Select 
          label='Escolha um dia da semana'
          options={dayOptions}
          onSelect={(value) => setSelectedWeekDay(value)}
          selected={selectedWeekDay}
          position='fixed'
        />
        
        {selectedWeekDay && (
          <Select 
            label='Escolha um de seus horários recorrentes'
            options={selectedWeekDay?.availabilities}
            onSelect={(value) => setSelectedHour(value as HourOption)}
            selected={selectedHour}
            position='fixed'
          />
        )}

        {loadingProfessionalRestrictions && (
          <S.SpinnerWrapper>
            <Spinner />
          </S.SpinnerWrapper>
        )}

        {!loadingProfessionalRestrictions && hasProfessionalRestrictions && (
          <NonRecurrentReservationBox
            patientUsername={patientUsername}
            restrictions={restrictions} 
            handleNonRecurrentReservationDate={handleNonRecurrentReservationDate}
          />
        )}

        {showSubmitButton && (
          <S.ButtonWrapper>
            <Typography variant='content3' center>
              *Brasil (UTC−3) - Horário de Brasília Padrão 00h00 a 23h59
            </Typography>
            <Button
              variant='contained'
              uppercase
              disabled={disableRescheduleReservationButton || updateReservation.isLoading}
              onClick={handleUpdateReservation}
            >
              {
                updateReservation.isLoading
                  ? <Spinner customClassName='customSpinner'/>
                  : 'Concluir Modificação'
              }
            </Button>
          </S.ButtonWrapper>
        )}
      </S.Container>
    )
  }